const ENERGY_FAMILIES = new Set([
  'extraction',
  'processing',
  'finishing',
  'manufacturing',
]);
const FAMILIES = new Set([
  ...ENERGY_FAMILIES,
  'packaging',
  'transport',
  'trimming',
]);

export const IMPACT_COLUMNS = [
  'water',
  'waterConsumption',
  'climateChange',
  'eutrophication',
  'fossilFuels',
  'ozoneDepletion',
  'humanToxicityCancerEffects',
  'humanToxicityNonCancerEffects',
  'particulateMatter',
  'ionizingRadiation',
  'photochemicalOzoneFormation',
  'acidification',
  'terrestrialEutrophication',
  'marineEutrophication',
  'freshwaterEcotoxicity',
  'landUse',
  'resourceUseMineralAndMetals',
];

const DOCUMENTATION_COLUMNS = {
  Description: 'description',
  'Primary source': 'primarySource',
  'Data generator': 'dataGenerator',
  'Impact generator': 'impactGenerator',
  'Original dataset': 'originalDataset',
  'System boundaries': 'systemBoundaries',
  Allocation: 'allocation',
  'Main assumptions': 'mainAssumptions',
  'Data validity': 'dataValidity',
  'Data quality': 'dataQuality',
  'Reference year': 'referenceYear',
  teR: 'teR',
  geR: 'geR',
  tiR: 'tiR',
  Precision: 'precision',
};

export const processOriginExportHeaders = (family) => {
  if (!FAMILIES.has(family))
    throw new Error(`Unknown process family: ${family}`);
  return [
    'Process name',
    'Origin',
    'Database context',
    'Database version',
    'Account name',
    'Type',
    family === 'trimming' ? 'Unit' : 'Functional unit',
    ...(ENERGY_FAMILIES.has(family) ? ['Energy type'] : []),
    'Creation date',
    'Update date',
    ...IMPACT_COLUMNS,
    ...Object.keys(DOCUMENTATION_COLUMNS),
  ];
};

const fetchMissingRelations = async (
  records,
  field,
  resource,
  getEmbedded,
  fetchRelatedRecords,
) => {
  const missing = records.filter(
    (record) => record[field] && !getEmbedded(record)?.name,
  );
  return missing.length ? fetchRelatedRecords(missing, field, resource) : {};
};

// Resolve names for the exact records supplied by React Admin's filtered export.
// IDs stay available to the list/edit screens and never become CSV columns or fallback values.
export async function buildProcessOriginExportRows(
  records,
  family,
  fetchRelatedRecords,
) {
  processOriginExportHeaders(family);
  const processField = `${family}Id`;
  const processLookup = await fetchMissingRelations(
    records,
    processField,
    family === 'trimming' ? 'trimming' : `process/${family}`,
    (record) => record[family],
    fetchRelatedRecords,
  );
  const processes = records.map((record) => ({
    ...record[family],
    ...processLookup[record[processField]],
  }));
  const versionRecords = processes.map((process) => ({
    databaseVersionId: process.databaseVersionId,
    databaseVersion: process.databaseVersion,
  }));
  const [origins, contexts, versions] = await Promise.all([
    fetchMissingRelations(
      records,
      'originId',
      'origins',
      (record) => record.origin,
      fetchRelatedRecords,
    ),
    fetchMissingRelations(
      records,
      'databaseContextId',
      'database-context',
      (record) => record.databaseContext,
      fetchRelatedRecords,
    ),
    fetchMissingRelations(
      versionRecords,
      'databaseVersionId',
      'databaseVersion',
      (record) => record.databaseVersion,
      fetchRelatedRecords,
    ),
  ]);

  return records.map((record, index) => {
    const process = processes[index];
    const row = {
      'Process name': process.name ?? '',
      Origin: record.origin?.name ?? origins[record.originId]?.name ?? '',
      'Database context':
        record.databaseContext?.name ??
        contexts[record.databaseContextId]?.name ??
        '',
      'Database version':
        process.databaseVersion?.name ??
        versions[process.databaseVersionId]?.name ??
        '',
      'Account name': record['Account name'] ?? '',
      Type: record.type ?? '',
      [family === 'trimming' ? 'Unit' : 'Functional unit']:
        (family === 'trimming' ? process.unit : process.functionalUnit) ?? '',
      ...(ENERGY_FAMILIES.has(family)
        ? { 'Energy type': record.energyType ?? '' }
        : {}),
      'Creation date': record.createdAt ?? '',
      'Update date': record.updatedAt ?? '',
    };
    IMPACT_COLUMNS.forEach((field) => {
      row[field] = record[field] ?? '';
    });
    Object.entries(DOCUMENTATION_COLUMNS).forEach(([label, field]) => {
      row[label] = record[field] ?? '';
    });
    return row;
  });
}

const escapeCsv = (value) => {
  const text = String(value ?? '');
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function serializeProcessOriginCsv(rows, family) {
  const headers = processOriginExportHeaders(family);
  return (
    '\uFEFF' +
    [
      headers.join(';'),
      ...rows.map((row) =>
        headers.map((header) => escapeCsv(row[header])).join(';'),
      ),
    ].join('\r\n')
  );
}

export const makeProcessOriginExporter =
  (family, filename = `${family}_origin`) =>
  async (records, fetchRelatedRecords) => {
    const rows = await buildProcessOriginExportRows(
      records,
      family,
      fetchRelatedRecords,
    );
    const csv = serializeProcessOriginCsv(rows, family);
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      URL.revokeObjectURL(url);
    }
  };
